import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import {
    getStatusStringsFromValue,
    DOSignalWidthDefault,
    maxDigitalChannels,
    modeTypeMap,
    OutputMode,
    OutputSetting,
    OutputSettingChannel,
    SignalType,
    signalTypeMap,
    SourceChannelCondition,
    TriggerCondition,
} from 'icreate/common/models/output-settings';
import { SignalWidthMax, SignalWidthMin } from 'icreate/common/models/alarm-group';
import { Vessel } from 'icreate/common/models/vessel';
import * as _ from 'lodash';
import { fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { SourceChannelSelectorComponent } from './source-channel-selector/source-channel-selector.component';
import { Channel } from 'icreate/common/models/channel';
import { AlarmPropertyList } from 'icreate/common/models/repose-group';
import { OutputSettingsService } from '@service/output-settings.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { MotorAlarmSetting } from 'icreate/common/models/motor-alarm-setting';
import { isDigital, isDigitalContact, isDigitalMotor } from 'icreate/common/models/channel-type';

@Component({
    selector: 'app-digital-output-setting',
    templateUrl: './digital-output-setting.component.html',
    styleUrl: './digital-output-setting.component.css',
    standalone: false,
})
export class DigitalOutputSettingComponent implements OnInit {
    readonly signalSelector = 'signal-type-selector';
    readonly modeSelector = 'mode-selector';
    readonly AND = SourceChannelCondition.AND;
    readonly OR = SourceChannelCondition.OR;

    @Input() setting: OutputSetting;
    @Input() vessel: Vessel;
    @Input() selectedSlot: SensorTagSlot;
    @Output() onCancel: EventEmitter<void> = new EventEmitter();
    @Output() onDelete: EventEmitter<void> = new EventEmitter();
    @ViewChild('signalSelElement') signalSelElement: ElementRef;
    @ViewChild('modeSelElement') modeSelElement: ElementRef;
    @ViewChild('settingChannels') settingChannels: ElementRef;

    initialSetting: OutputSetting;
    isEditing: boolean = false;
    isSignalTypeOpen: boolean = false;
    isOutputModeOpen: boolean = false;
    isTableExpanded: boolean = false;
    isInfoMessageVisible: boolean = false;
    isLoading: boolean = false;
    optionsIndex: number;
    optionsTop: string;
    optionsLeft: string;

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', [])
    clickOut() {
        this.isSignalTypeOpen = false;
        this.isOutputModeOpen = false;
        this.isInfoMessageVisible = false;
    }

    constructor(
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private outputSettingsService: OutputSettingsService,
        private messageDialogService: MessageDialogService
    ) {}

    ngOnInit(): void {
        if (!this.setting.id) this.isEditing = true;
        else if (this.setting.channels.length > 0) {
            this.setting.channels = this.setting.channels.map(channel => {
                if (channel.triggerCondition != TriggerCondition.STATUS) return channel;
                const settingChannel = channel.channel;
                if (!settingChannel) return channel;
                if (!isDigital(settingChannel.fcuData.channelType)) return channel;
                if (isDigitalContact(settingChannel.fcuData.channelType)) {
                    const contactOnString = settingChannel.fcuData.digitalAlarmSetting.statusStringContactOn;
                    const contactOffString = settingChannel.fcuData.digitalAlarmSetting.statusStringContactOff;

                    if (channel.statusValue == 1) channel.statusCondition = [contactOnString];
                    else if (channel.statusValue == 0) channel.statusCondition = [contactOffString];
                    else channel.statusCondition = [];
                } else if (isDigitalMotor(settingChannel.fcuData.channelType)) {
                    channel.statusCondition = getStatusStringsFromValue(
                        channel.statusValue,
                        settingChannel.fcuData.motorChannelSetting
                    );
                }
                return channel;
            });
        }
        this.initialSetting = _.cloneDeep(this.setting);

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isSignalTypeOpen = false;
                    this.isOutputModeOpen = false;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isSaveEnabled() {
        if (!this.setting.outputMode || !this.setting.signalType || this.setting.channels.length == 0) return false;
        if (this.isOneShot && !this.isSignalWidthValid()) return false;
        if (this.setting.channels.length > 1 && !this.hasSourceCondition()) return false;

        return !_.isEqual(this.initialSetting, this.setting);
    }

    get isOneShot() {
        return this.setting.signalType == SignalType.ONE_SHOT;
    }

    isSignalWidthValid() {
        const width = this.setting.signalWidth;
        return width >= SignalWidthMin && width <= SignalWidthMax;
    }

    handleSignalWidthChange(width) {
        this.setting.signalWidth = Number(width);
    }

    hasSourceCondition() {
        return (
            this.setting.sourceChannelCondition &&
            (this.setting.sourceChannelCondition == SourceChannelCondition.AND ||
                this.setting.sourceChannelCondition == SourceChannelCondition.OR)
        );
    }

    async save() {
        if (!this.isSaveEnabled()) return;
        this.isLoading = true;
        try {
            const { settingId } = await this.outputSettingsService.saveDOTerminals(this.setting, this.vessel.id);
            if (!this.setting.id) this.setting.id = settingId;
            this.isEditing = false;
            this.initialSetting = _.cloneDeep(this.setting);
            this.showUpdatedMessage();
        } catch (err) {
            console.error(err);
        }
        this.isLoading = false;
    }

    async cancel() {
        const shouldLeave = await this.shouldLeaveConfirmation();
        if (!shouldLeave) return;
        if (this.setting.id) {
            this.setting = _.cloneDeep(this.initialSetting);
            this.isEditing = false;
        } else {
            this.setting = _.cloneDeep(this.initialSetting);
            this.onCancel.emit();
        }
    }

    async shouldLeaveConfirmation() {
        let shouldLeave = true;
        if (!_.isEqual(this.initialSetting, this.setting)) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'output-settings.DO.settings.cancel.title',
                    backButton: 'output-settings.DO.settings.cancel.cancel',
                    discardButton: 'output-settings.DO.settings.cancel.discard',
                    saveButton: 'output-settings.DO.settings.cancel.save',
                    redDiscard: true,
                    saveDisabled: !this.isSaveEnabled(),
                },
            });

            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.GO_BACK) shouldLeave = false;
            else if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (this.isSaveEnabled()) await this.save();
            }
        }

        return shouldLeave;
    }

    async deleteSetting() {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'output-settings.DO.delete.title',
                message: 'output-settings.DO.delete.message',
                message2: 'output-settings.DO.delete.message-2',
                discardButton: 'output-settings.DO.delete.cancel',
                saveButton: 'output-settings.DO.delete.confirm',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) this.onDelete.emit();
    }

    openSignalSelector(event) {
        this.isOutputModeOpen = false;
        if (this.isSignalTypeOpen) {
            this.updatePosition();
            this.isSignalTypeOpen = false;
        } else {
            this.updatePosition(this.signalSelElement);
            this.isSignalTypeOpen = true;
        }
        event.stopPropagation();
    }

    openModeSelector(event) {
        this.isSignalTypeOpen = false;
        if (this.isOutputModeOpen) {
            this.updatePosition();
            this.isOutputModeOpen = false;
        } else {
            this.updatePosition(this.modeSelElement);
            this.isOutputModeOpen = true;
        }
        event.stopPropagation();
    }

    getSelectorOptions(selector) {
        if (selector == this.signalSelector) {
            const signalTypeStrings = [];
            signalTypeMap.forEach((value, key) => signalTypeStrings.push(key));
            return signalTypeStrings;
        } else if (selector == this.modeSelector) {
            const modeStrings = [];
            modeTypeMap.forEach((value, key) => {
                if (
                    this.setting.signalType == SignalType.ON_OFF &&
                    (key == OutputMode.FUNCTIONAL_TEST || key == OutputMode.BOTH)
                )
                    return;

                modeStrings.push(key);
            });
            return modeStrings;
        }
    }

    optionSelected(selector, option) {
        if (selector == this.signalSelector) {
            this.setting.signalType = option;
            if (option == SignalType.ONE_SHOT) {
                if (!this.setting.signalWidth) this.setting.signalWidth = DOSignalWidthDefault;
            } else this.setting.signalWidth = null;
            if (option == SignalType.ON_OFF && this.setting.outputMode) {
                if (
                    this.setting.outputMode == OutputMode.BOTH ||
                    this.setting.outputMode == OutputMode.FUNCTIONAL_TEST
                ) {
                    this.setting.outputMode = null;
                }
            }
        } else if (selector == this.modeSelector) {
            this.setting.outputMode = option;
        }
    }

    getOptionString(selector, option) {
        if (selector == this.signalSelector) {
            return signalTypeMap.get(option);
        } else if (selector == this.modeSelector) {
            return modeTypeMap.get(option);
        }
    }

    hasNoChannels(): boolean {
        return !this.setting?.channels || this.setting.channels.length === 0;
    }

    isExpandDisabled(): boolean {
        return !this.setting || !this.settingChannels?.nativeElement || this.setting.channels.length < 3;
    }

    async showChannelDialog(channel?: OutputSettingChannel) {
        const dialogRef = this.dialog.open(SourceChannelSelectorComponent, {
            width: '460px',
            data: {
                vesselId: this.vessel.id,
                settingChannel: channel,
            },
            disableClose: true,
        });

        const digitalSettingChannel: {
            selectedChannel: Channel;
            triggerCondition: TriggerCondition;
            alarmConditionsSet: Set<AlarmPropertyList>;
            statusConditionsSet: Set<string>;
            statusValue: number;
        } = await lastValueFrom(dialogRef.afterClosed());

        return digitalSettingChannel;
    }

    async addChannel() {
        const digitalSettingChannel = await this.showChannelDialog();
        if (!digitalSettingChannel) return;

        const newIndex = this.setting.channels.length + 1;

        this.setting.channels.push({
            channelId: digitalSettingChannel.selectedChannel.id,
            settingId: this.setting.id,
            channel: digitalSettingChannel.selectedChannel,
            index: newIndex,
            triggerCondition: digitalSettingChannel.triggerCondition,
            alarmCondition: [...digitalSettingChannel.alarmConditionsSet],
            statusCondition: [...digitalSettingChannel.statusConditionsSet],
            statusValue: digitalSettingChannel.statusValue,
        });

        this.cdr.detectChanges();

        this.settingChannels?.nativeElement?.scrollTo({
            top: this.settingChannels.nativeElement.scrollHeight,
            behavior: 'smooth',
        });
    }

    async updateChannel(index: number) {
        const channel = this.setting.channels.find(channel => channel.index == index);
        if (!channel) return;

        const digitalSettingChannel = await this.showChannelDialog(channel);
        if (!digitalSettingChannel) return;

        this.setting.channels[index - 1] = {
            channelId: digitalSettingChannel.selectedChannel.id,
            settingId: this.setting.id,
            index: channel.index,
            channel: digitalSettingChannel.selectedChannel,
            triggerCondition: digitalSettingChannel.triggerCondition,
            alarmCondition: [...digitalSettingChannel.alarmConditionsSet],
            statusCondition: [...digitalSettingChannel.statusConditionsSet],
            statusValue: digitalSettingChannel.statusValue,
        };
    }

    isAddChannelDisabled(): boolean {
        return !this.setting.channels || this.setting.channels.length >= maxDigitalChannels;
    }

    getChannelName(settingChannel: OutputSettingChannel) {
        const channel = settingChannel.channel;
        return `${channel.fcuData.fcuChannelId} ${channel.opsData.name}`;
    }

    async removeChannel(index: number) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                message: 'output-settings.AO.clear.message',
                message2: 'output-settings.AO.clear.message-2',
                discardButton: 'output-settings.AO.clear.cancel',
                saveButton: 'output-settings.AO.clear.confirm',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            this.setting.channels.splice(index, 1);
        }

        if (this.setting.channels.length <= 1) {
            this.setting.sourceChannelCondition = SourceChannelCondition.UNDEFINED;
        }
    }

    showOptions(index: number) {
        if (this.optionsIndex !== index) {
            this.optionsIndex = index;
        } else this.optionsIndex = null;
    }

    getTriggerCondition(settingChannel: OutputSettingChannel) {
        if (settingChannel.triggerCondition == TriggerCondition.ALARM) {
            return 'output-settings.DO.dialog.trigger-condition.alarm';
        } else if (settingChannel.triggerCondition == TriggerCondition.STATUS) {
            return 'output-settings.DO.dialog.trigger-condition.status';
        }
    }

    getCondition(settingChannel: OutputSettingChannel) {
        if (settingChannel.triggerCondition == TriggerCondition.ALARM) {
            const statusStrings = settingChannel.alarmCondition.map(setting => {
                return settingChannel.channel.fcuData[setting].statusString;
            });
            return statusStrings.join(', ');
        } else if (settingChannel.triggerCondition == TriggerCondition.STATUS) {
            return settingChannel.statusCondition.join(', ');
        }
    }

    shouldEnableSourceCondition() {
        return this.setting.channels.length > 1;
    }

    isOrCondition() {
        return this.setting.sourceChannelCondition == SourceChannelCondition.OR;
    }

    isAndCondition() {
        return this.setting.sourceChannelCondition == SourceChannelCondition.AND;
    }

    updateType(event, condition: SourceChannelCondition) {
        if (!this.shouldEnableSourceCondition()) return;
        this.setting.sourceChannelCondition = condition;
        event.stopImmediatePropagation();
        this.cdr.detectChanges();
    }

    showUpdatedMessage() {
        this.messageDialogService.openMessage('output-settings.DO.notification.updated', {
            num: this.outputSettingsService.paddNumber(this.setting.terminalNum),
        });
    }

    private updatePosition(elementRef?) {
        if (!elementRef) {
            this.optionsTop = null;
            this.optionsLeft = null;
            return;
        }
        const selectorPosition = elementRef.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top + 1 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 'px';
    }
}
